import type {EagColumn} from '../../../../../types/EagColumn';
import type {EagColumnState} from '../../../../../types/EagColumnState';
import type {Filters} from '../../../../../types/Filters';
import {isFilterValueActive} from '../../../../../utils/isFilterValueActive';
import type {LineHeight} from '../../../../../utils/lineHeightToValue';

type HeaderCellLayoutParams = {
  column: EagColumn;
  columnState: EagColumnState;
  width: number;
  headerHeight: LineHeight;
  showColumnMenu: boolean;
  columnSortMode: string;
  isMobile: boolean;
  filterState: Filters;
};

type HeaderCellLayout = {
  useSmallButtons: boolean;
  buttonSize: number;
  showMenuButton: boolean;
  showSortButton: boolean;
  textPaddingRight: number;
  isColumnFilterActive: boolean;
  menuPriority: 'primary' | undefined;
  columnHasMenuContent: boolean;
};

const BUTTON_PAD_RIGHT = 4; // pr-1
const CELL_PADDING_LEFT = 8; // px-2
const MIN_TEXT_WIDTH = 8; // ~1 char at text-xs

/**
 * Computes button visibility, sizing, and text padding for a header cell.
 *
 * Progressive hiding: menu button hides first, then sort button,
 * based on available text width after subtracting button widths.
 */
export const computeHeaderCellLayout = (params: HeaderCellLayoutParams): HeaderCellLayout => {
  const useSmallButtons =
    params.headerHeight === 'COMPACT' ||
    params.headerHeight === 'SHORT' ||
    params.headerHeight === 'DEFAULT';
  const buttonSize = useSmallButtons ? 24 : 32;

  const oneButtonWidth = buttonSize + BUTTON_PAD_RIGHT;

  const textSpaceWithOne = params.width - oneButtonWidth - CELL_PADDING_LEFT;

  // Column-specific menu content flag — still used on mobile (hamburger menu).
  const columnHasMenuContent =
    params.column.sortable === true ||
    params.column.filter != null ||
    params.columnState.pinned != null;

  // Menu button is revealed on header hover (desktop) — HeaderCellActions keeps it
  // hidden until hover, menu-open, or active filter. Suppressed on columns too
  // narrow for the button to leave any readable text. The column menu stays
  // accessible via right-click context menu and the mobile hamburger as well.
  const showMenuButton =
    params.showColumnMenu && columnHasMenuContent && textSpaceWithOne >= MIN_TEXT_WIDTH;

  // Sort icon only visible when the column is actively sorted.
  const isSortActive = params.columnState.sort != null;
  const showSortButton =
    isSortActive && params.columnSortMode !== 'NONE' && textSpaceWithOne >= MIN_TEXT_WIDTH;

  const isColumnFilterActive = isFilterValueActive(params.filterState[params.column.key]);
  const menuPriority = isColumnFilterActive ? ('primary' as const) : undefined;

  // Right padding reserves space for the ALWAYS-VISIBLE absolute overlays at the
  // cell's right edge, summed left → right: the mobile hamburger, the desktop menu
  // button when an active filter keeps it permanently shown, and the sort indicator
  // (rightmost element of the actions cluster). The hover-revealed desktop menu
  // button (no active filter) reserves nothing — covering the tail of truncated
  // text while hovering is an accepted trade-off.
  const hamburgerReserve = params.isMobile && columnHasMenuContent ? oneButtonWidth : 0;
  const activeFilterMenuReserve =
    !params.isMobile && showMenuButton && isColumnFilterActive ? oneButtonWidth : 0;
  const sortIndicatorReserve = showSortButton ? oneButtonWidth : 0;
  const textPaddingRight = hamburgerReserve + activeFilterMenuReserve + sortIndicatorReserve;

  return {
    useSmallButtons,
    buttonSize,
    showMenuButton,
    showSortButton,
    textPaddingRight,
    isColumnFilterActive,
    menuPriority,
    columnHasMenuContent,
  };
};
